import { MIN_REVIEWS_TO_SCORE, TRUST_SCORE_WEIGHTS } from "./methodology";

/**
 * The questions a sceptical reader actually asks, answered once.
 *
 * Defined here rather than in the page because the FAQPage structured data has
 * to say the same words the visitor sees — Google treats a mismatch as a
 * violation, and it would also just be dishonest. The page renders this array
 * and the head serialises the same array.
 *
 * Every answer has to be checkable against the running site or the code. None
 * of them may describe an intention.
 */
export interface QA {
  q: string;
  a: string;
}

// Read from the published weights so the answer cannot drift from the formula.
const weightSummary = TRUST_SCORE_WEIGHTS.map(
  (w) => `${w.label.toLowerCase()} ${Math.round(w.weight * 100)}%`,
).join(", ");

export const METHODOLOGY_FAQ: QA[] = [
  {
    q: "How do you know a reviewer actually used the product?",
    a:
      "They prove it, cryptographically. A reviewer can sign a message from a wallet that holds or held the token, connect a read-only exchange API key that shows account activity, or demonstrate that their address interacted with a protocol's contracts. The signature authorises no transaction and moves no funds, and exchange secrets are verified and then discarded rather than stored.",
  },
  {
    q: "Can a company pay for a better rating?",
    a:
      "No. A company can claim its profile, correct its details and reply publicly to any review. It cannot buy a rating, a Trust Score, a ranking position, or the removal of criticism. The code that ranks and scores listings is never passed any billing information, so it has no way to know who pays, and automated tests fail the build if anyone connects the two.",
  },
  {
    q: "Are unverified reviews published?",
    a:
      "Yes, and they are labelled as unverified rather than hidden. They are rate-limited more aggressively and weighted far lower than proven ones. Ratings are shown split into verified and unverified so a proven customer's experience never disappears into an anonymous crowd.",
  },
  {
    q: "Why do some listings have no rating at all?",
    a:
      `A listing needs at least ${MIN_REVIEWS_TO_SCORE} published reviews before a score is calculated. Below that it shows no rating rather than a number built on one opinion. An absent score means nobody has reviewed it yet — it is not a low score and should not be read as one.`,
  },
  {
    q: "What goes into the Trust Score?",
    a:
      `It combines the weighted rating with objective signals rather than opinion alone: ${weightSummary}. The weights and the version of the formula are published, and the inputs are public data such as contract addresses, automated security scans and on-chain history.`,
  },
  {
    q: "Can a company delete a review it does not like?",
    a:
      "No. A company can reply in public and can dispute a review, which sends it to moderation. If a review is removed for breaking the rules, the page keeps a tombstone saying so instead of quietly deleting it, and a removed review no longer counts towards any score.",
  },
  {
    q: "Where do the listings come from?",
    a:
      "Most were added by staff from public sources: exchange and wallet company details, and tokens drawn from market-cap rankings with every contract address confirmed against a live chain scanner before it was written. Anyone signed in can also suggest a listing, and a moderator reviews it before it appears.",
  },
  {
    q: "Is anything here financial advice?",
    a:
      "No. CryptoChit publishes user opinions and verifiable on-chain facts. It does not provide investment advice, price predictions, or recommendations to buy or sell anything.",
  },
];

/** FAQPage structured data for the same questions the page shows. */
export function faqJsonLd(items: QA[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
